<script lang="ts" setup>
    import '../../../css/choice.css';     
    import {onMounted,reactive,computed,ref} from 'vue'; 
    import {popAlert,ModalStyles} from '../../lib/Notification'; 
    import {AlignmentType,ButtonDisplaySize,ButtonDisplayStyle} from '../../Enums/UiEnums';
    import {ActionButtonStyle} from '../../lib/Types/Types';
    import WindowHeaderFooter from '../../components/WindowHeaderFooter.vue'; 
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'; 
    import {hideWindowObject} from '../../lib/Models/CommonItemsModel';   
    import {ErrorReturnMessage,getMissingClaimInformation,setClaimInsurance} from '../../lib/Api/ClaimInsurance'; 
    import {IResultAndError} from '../../lib/ChoiceApi'; 
    import {IMissingClaimInsurance,IClaimInsuranceList} from '../../lib/Models/ClaimInsuranceModel';   
    import {ICol, RenderModeType}  from "../../lib/ChoiceTableHelpers"; 
    import ChoiceTable from "../../components/ChoiceTable.vue"; 
     
    const noIncomingClaims = computed(() => !formData.IncomingClaimsIds?.length);
    const saveDisabled = computed<boolean>(() => !goodClaimData.value.filter(item => item.isSelected)?.length);
    const saveClaimCount = computed<number>(() => goodClaimData.value.filter(item => item.isSelected)?.length ?? 0);
    const badClaimCount = computed<number>(() => badClaimData.value?.length ?? 0);
    
    const goodClaimTable = ref<InstanceType<typeof ChoiceTable>>();
    const goodClaimData = ref<(IMissingClaimInsurance)[]>([]);
    const goodClaimColumns = ref<ICol[]>([ 
        { Name: '', DataField:"isSelected", RenderMode: RenderModeType.Selection },
        { Name: 'Claim', DataField: 'claimID'}, 
        { Name: 'DOS', DataField: 'dos',Alignment: AlignmentType.Center},  
        { Name: 'Chart', DataField: 'patientID' }, 
        { Name: 'Patient Name', DataField: 'patientName' },  
        { Name: 'Ins. Profile Description', DataField: 'insuranceProfileDescription'}, 
        { Name: 'Primary Name', DataField: 'primaryPayerName' },
        { Name: 'Secondary Name', DataField: 'secondaryPayerName' },
        { Name: 'Tertiary Name', DataField: 'tertiaryPayerName' }
    ]);        
    const badClaimTable = ref<InstanceType<typeof ChoiceTable>>();
    const badClaimData = ref<(IMissingClaimInsurance)[]>([]);
    const badClaimColumns = ref<ICol[]>([ 
        { Name: 'Claim', DataField: 'claimID'}, 
        { Name: 'DOS', DataField: 'dos',Alignment: AlignmentType.Center},  
        { Name: 'Chart', DataField: 'patientID' }, 
        { Name: 'Patient Name', DataField: 'patientName' },  
        { Name: 'Ins. Profile Description', DataField: 'insuranceProfileDescription'}
    ]);        
    const formData = reactive <
    {       
        IncomingClaimsIds?: string[]
    }>({    
    });
    const props = withDefaults(defineProps<{  
        writeEnabled: boolean,
        list?: string
    }>(),
    {
        writeEnabled: false,
        list: ""
    });
    onMounted(async () => {   
        hideWindowObject("LoadingMessage");   
        if (props.list) {
            formData.IncomingClaimsIds = props.list
                .split(",")
                .map(id => id.trim())
                .filter(id => /^\d+$/.test(id)); 
            getSetupInformation();
        }
    });
     
    async function closeWindow() : Promise<void> {  
        window.close();
    }   
    async function SaveMissingClaimInsurances() : Promise<void> {  
        const alertTitle = "Save Claim Insurances";
        const selectedClaimIds: IClaimInsuranceList = {
            ClaimList: goodClaimData.value
                .filter(item => item.isSelected)
                .map(item => ({
                    ClaimID: item.claimID,
                    PrimaryPayerEID: item.primaryPayerEID ?? 0,
                    SecondaryPayerEID: item.secondaryPayerEID ?? 0,
                    TertiaryPayerEID: item.tertiaryPayerEID ?? 0
                }))
        };
        if (!selectedClaimIds) {
            await popAlert("Please select at least one claim to process.",alertTitle,ModalStyles.danger);
            return;
        }
        const response = await setClaimInsurance(selectedClaimIds);
        if (!response.success) {
            await popAlert((response.error?.error ?? ErrorReturnMessage.claimInsuranceUpdate),alertTitle,ModalStyles.danger);
            return;
        }
        if (response.success) {
            if (response.success.errorCode === 0) { 
                await popAlert("Claim(s) updated successfully.",alertTitle,ModalStyles.info);
                closeWindow();
                return;
            }
            await popAlert((response.success.errorMessage ?? ErrorReturnMessage.claimInsuranceUpdate),alertTitle,ModalStyles.danger);
            return;
        } 
        await popAlert((response.error?.error ?? ErrorReturnMessage.claimInsuranceUpdate),alertTitle,ModalStyles.danger);
    }
    async function getSetupInformation() : Promise<void> {  
        if (!formData.IncomingClaimsIds || formData.IncomingClaimsIds.length === 0) {
            return;
        }       
        const result: IResultAndError<IMissingClaimInsurance[]> = await getMissingClaimInformation(formData.IncomingClaimsIds);
        if (!result.success) {
            await popAlert((result.error?.error ?? ErrorReturnMessage.claimInsuranceSetup),"Setup Details",ModalStyles.danger);
            return;
        }
        goodClaimData.value = result.success.filter(item => item.isSelected) ?? [];
        badClaimData.value = result.success.filter(item => !item.isSelected) ?? [];
    }
</script> 
<template> 
    <WindowHeaderFooter
    :headerHeight="3.5"
    :footerHeight="3.5">
        <template v-slot:WindowHeader>
            <div class="isalus-page-title-large pt-2 pb-2">Missing Claim Insurances</div>
        </template>
        <template v-slot:WindowBody>        
            <div v-if="noIncomingClaims" class="row pt-2"> 
                <div class="col-12 label-isalus-medium text-danger">
                    No claims were found to process. Please check your process and try again.
                </div> 
            </div> 
            <div v-if="goodClaimData.length > 0" class="row pt-2">
                <div class="col-12 label-isalus-small text-primary">
                    The {{ saveClaimCount.toLocaleString() }} selected claims below will have their primary, secondary, and tertiary insurance replaced with the insurance(s) shown from the patient’s default insurance profile.
               </div> 
            </div> 
            <div v-if="goodClaimData.length > 0" class="row pt-2">
                <div class="col-12" style="font-size: .75rem;">
                    <ChoiceTable 
                        ref="goodClaimTable"
                        :columns="goodClaimColumns" 
                        :data="goodClaimData" 
                        :show-filters="false" 
                        :show-footer="false" 
                        :allowSorting="true"
                        :key-value-data-member="'procedureCode'">
                    </ChoiceTable>
                </div>
            </div>
            <div v-if="badClaimCount > 0" class="row pt-2">
                <div class="col-12 label-isalus-small text-danger">
                   We could not associate the {{ badClaimCount.toLocaleString() }} claim(s) below with any insurance from the patient's insurance profile.
                </div> 
            </div> 
            <div v-if="badClaimCount > 0" class="row pt-2">
                <div class="col-12" style="font-size: .75rem;">
                    <ChoiceTable 
                        ref="badClaimTable"
                        :columns="badClaimColumns" 
                        :data="badClaimData" 
                        :show-filters="false" 
                        :show-footer="false" 
                        :allowSorting="true"
                        :key-value-data-member="'procedureCode'">
                    </ChoiceTable>
                </div>
            </div>
        </template>
        <template v-slot:WindowFooter>
            <div class="row">
                <div class="col-9 label-isalus-small">
                    <span class="text-primary">{{saveClaimCount.toLocaleString()}} claim(s) selected for processing.</span>
                </div>
                <div class="col-3 text-end ">
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Save"
                        :text="'Save'"
                        :spinText="'Save'"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                        :performAction="SaveMissingClaimInsurances" 
                        :endingPadding="1"
                        :disabled="saveDisabled"
                    /> 
                    <ActionButtonV2 
                        :action="ActionButtonStyle.Close"
                        :text="'Close'"
                        :spinText="'Close'"
                        :size="ButtonDisplaySize.Small" 
                        :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                        :performAction="closeWindow"
                    /> 
                </div>
            </div>
        </template>
    </WindowHeaderFooter> 
</template>
